// Núcleo de recuperação do app — sem React, para ser usado tanto pela tela de
// erro do router (app-updater) quanto pelo error boundary global sem ciclo de
// import: detecção de chunk faltante, guardas anti-loop de reload e limpeza de
// cache.

export const RELOAD_WINDOW_MS = 10_000; // não recarrega de novo dentro deste intervalo

// Mensagens de falha de import dinâmico variam por browser. Casamos qualquer uma
// (case-insensitive); o resto é bug de verdade e NÃO deve virar "Atualizando…".
const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module", // Chrome/Edge
  "error loading dynamically imported module", // Firefox
  "importing a module script failed", // Safari
  "unable to preload css", // Vite preload de CSS
  "chunkloaderror", // alguns loaders/browsers
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const lower = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => lower.includes(p));
}

// Guarda anti-loop: se o deploy está quebrado ou o usuário offline, um reload
// automático em loop seria infinito. Só recarrega na 1ª falha ou após a janela.
export function shouldReload(now: number, lastReloadAt: number | null): boolean {
  if (lastReloadAt === null) return true;
  return now - lastReloadAt > RELOAD_WINDOW_MS;
}

export const RELOAD_GUARD_KEY = "app-reload-at"; // guarda do reload por chunk faltante
export const VERSION_RELOAD_GUARD_KEY = "app-version-reload-at"; // guarda do reload por versão nova

// Chaves do localStorage que sobrevivem à limpeza. O tema é preferência do
// usuário, não estado corrompível — apagá-lo faria a tela piscar de branco no
// reload sem resolver nada.
export const PRESERVED_LOCAL_STORAGE_KEYS = ["lai-disk-preferences"];

// Guardas anti-loop: sem elas, um deploy quebrado faria
// "limpar cache → recarregar → erro → limpar cache" para sempre.
export const PRESERVED_SESSION_STORAGE_KEYS = [RELOAD_GUARD_KEY, VERSION_RELOAD_GUARD_KEY];

export function localStorageKeysToClear(keys: string[]): string[] {
  return keys.filter((k) => !PRESERVED_LOCAL_STORAGE_KEYS.includes(k));
}

// Limpa tudo que pode estar guardando bytes de uma versão antiga: Cache Storage
// (assets), service workers, sessionStorage e o localStorage do app (menos o
// tema). Cada passo é isolado — storage bloqueado ou API ausente não pode
// impedir o reload, que é o objetivo final do botão.
export async function clearAppCaches(): Promise<void> {
  try {
    if ("caches" in globalThis) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch {
    /* sem Cache Storage: segue */
  }
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
  } catch {
    /* sem service worker: segue */
  }
  try {
    const preserved = PRESERVED_SESSION_STORAGE_KEYS.map(
      (key) => [key, sessionStorage.getItem(key)] as const,
    );
    sessionStorage.clear();
    for (const [key, value] of preserved) if (value !== null) sessionStorage.setItem(key, value);
  } catch {
    /* storage bloqueado: segue */
  }
  try {
    // Enumerado por length/key() (API do Storage) em vez de Object.keys: é o
    // contrato padrão e não depende do objeto ser enumerável.
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key !== null) keys.push(key);
    }
    for (const key of localStorageKeysToClear(keys)) localStorage.removeItem(key);
  } catch {
    /* storage bloqueado: segue */
  }
}

// Limpa e recarrega. Sem querystring de cache-busting de propósito: rotas com
// validateSearch rejeitariam o parâmetro extra, e o index.html já é servido com
// `no-store` (Caddyfile) — o que restava em cache some no clearAppCaches().
export async function clearCacheAndRestart(): Promise<void> {
  await clearAppCaches();
  location.reload();
}

// Leitura tolerante de uma guarda anti-loop (sessionStorage pode estar bloqueado).
export function readReloadGuard(key: string): number | null {
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw === null ? null : Number(raw);
    return parsed === null || Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

export function writeReloadGuard(key: string, now: number): void {
  try {
    sessionStorage.setItem(key, String(now));
  } catch {
    /* storage bloqueado: o reload acontece mesmo assim */
  }
}
